import { observable, action, runInAction, makeObservable } from "mobx";
import { ColorStyle } from "src/utils/Colors";
import { Announcement, Notification } from "src/types";
import { RootStore } from "./RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import { GetNewsDetailsById_newsitems_newsfiles as db_newsfiles } from "src/api/generated/GetNewsDetailsById";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { UpsertUsersetting, UpsertUsersettingVariables } from "src/api/generated/UpsertUsersetting";
import { UPSERT_USERSETTING } from "src/api/customer";
import { ums_usersettings_insert_input } from "src/api/generated/globalTypes";
import { SupportedLanguage } from "src/utils/language";

const headerHeights = {
    mobile: 70,
    tablet: 80,
    desktop: 80
};

export enum ScreenWidth {
    SM = 576,
    MD = 768,
    LG = 992,
    XL = 1200
}

export enum RealestatesListType {
    GRID,
    LIST
}

// As defined by chrome dev tools
export enum SCREEN_SIZE {
    MOBILE_S,
    MOBILE_M,
    MOBILE_L,
    TABLET,
    LAPTOP,
    LAPTOP_L,
    DESKTOP
}

export const DEFAULT_INVOICE_PANE_WIDTH: number = 700;
export const DEFAULT_INVOICE_PANE_WIDTH_RATIO: number = 0.6;

export const DEFAULT_AGENDAITEM_PANE_WIDTH: number = 350;
export const DEFAULT_AGENDAITEM_PANE_WIDTH_RATIO: number = 0.2;

export class UIStore {
    rootStore: RootStore;

    showNavigationBar = true;
    isMobile = false;
    showSearchbar = false;
    closeAppbar = false;
    headerHeight = 80;
    navigationBarHeight = 52;
    backgroundColor = ColorStyle("white");
    realestatesViewType = RealestatesListType.GRID;
    financeListSelectionType: number | undefined = undefined;
    financeListSelectionId: string | undefined = undefined;
    financeListExpandedItems: string[] | undefined = undefined;
    shouldShowLoginNotification: boolean = false;
    notifications: Notification[] = [];
    displayedNotifications: number[] = [];
    showSearchAppbarButton: boolean = true;
    showProfileAppbarButton: boolean = true;
    hasNewNewsData: boolean = true;
    hasNewAssembliesData: boolean = true;
    rerenderNavigationBar: boolean = false;
    currentAnnouncement: Announcement | undefined;
    wasAnnouncementDownloaded: boolean = false;
    language: SupportedLanguage | undefined = undefined;
    contentWidth: number = 0;
    isLegendDisplayed: boolean = true;
    isNewsInsertedEventLoaded: boolean = true;
    isAddNewsDocumentClicked: boolean = false;
    isNewsPublishModalDisplayed: boolean = false;
    isCustomerUserReset: boolean = false;
    dbNewsFiles: db_newsfiles[] = [];
    tenantItemName: string | undefined = undefined;
    isDemoModeTicketingActive: boolean = false;
    currentScreenSize: SCREEN_SIZE = SCREEN_SIZE.DESKTOP;
    agendaItemPaneWidthRatio: number = 0;
    invoiceFormContainerHeight: number = 0;
    invoiceFormHeight: number = 0;
    invoiceFilePreviewWidth: number = 0;
    invoicePaneWidth: number = DEFAULT_INVOICE_PANE_WIDTH; // only will be set if splitview is displayed
    invoicePaneScreenSize: SCREEN_SIZE = SCREEN_SIZE.DESKTOP;
    invoicePaneWidthRatio: number = 0;
    splitPaneWidth: number = 0;
    isResettingInvoicePaneWidth: boolean = false;
    isResizeObserverDisabled: boolean = false;
    isHeaderDisplayed: boolean = true;

    mobileWidth = 576;
    tabletWidth = 768;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        runInAction(() => {
            window.addEventListener("resize", this.onWindowResize);
            this.setIsMobile(window.innerWidth < 576);

            this.currentScreenSize = this.calculateScreenSize(window.innerWidth);

            this.setHeaderHeight(this.getHeaderHeight());
        });
        makeObservable(this, {
            showNavigationBar: observable,
            isMobile: observable,
            showSearchbar: observable,
            closeAppbar: observable,
            headerHeight: observable,
            navigationBarHeight: observable,
            backgroundColor: observable,
            realestatesViewType: observable,
            financeListSelectionType: observable,
            financeListSelectionId: observable,
            financeListExpandedItems: observable,
            shouldShowLoginNotification: observable,
            notifications: observable,
            displayedNotifications: observable,
            showSearchAppbarButton: observable,
            showProfileAppbarButton: observable,
            hasNewNewsData: observable,
            hasNewAssembliesData: observable,
            rerenderNavigationBar: observable,
            currentAnnouncement: observable,
            wasAnnouncementDownloaded: observable,
            language: observable,
            contentWidth: observable,
            isLegendDisplayed: observable,
            isNewsInsertedEventLoaded: observable,
            isAddNewsDocumentClicked: observable,
            isNewsPublishModalDisplayed: observable,
            isCustomerUserReset: observable,
            dbNewsFiles: observable,
            tenantItemName: observable,
            isDemoModeTicketingActive: observable,
            currentScreenSize: observable,
            agendaItemPaneWidthRatio: observable,
            invoiceFormContainerHeight: observable,
            invoiceFormHeight: observable,
            invoiceFilePreviewWidth: observable,
            invoicePaneWidth: observable,
            invoicePaneScreenSize: observable,
            invoicePaneWidthRatio: observable,
            splitPaneWidth: observable,
            isResettingInvoicePaneWidth: observable,
            isResizeObserverDisabled: observable,
            isHeaderDisplayed: observable,
            setAgendaItemPaneWidthRatio: action,
            setInvoicePaneWidthRatio: action,
            setSplitPaneWidth: action,
            setIsResettingInvoicePaneWidth: action,
            setIsResizeObserverDisabled: action,
            resetInvoicePaneWidth: action,
            setInvoicePaneWidth: action,
            saveInvoicePaneWidthUserSetting: action,
            setInvoiceFormContainerHeight: action,
            setInvoiceFormHeight: action,
            setInvoiceFilePreviewWidth: action,
            setShowProfileAppbarButton: action,
            setShowSearchAppbarButton: action,
            setSearchbar: action,
            setIsMobile: action,
            setHeaderHeight: action,
            setCloseAppBar: action,
            setBackgroundColor: action,
            setFinanceListExpandedItems: action,
            setFinanceListSelectionId: action,
            setRealestatesViewType: action,
            setShouldShowLoginNotification: action,
            setRerenderNavigationBar: action,
            enqueueSnackbar: action,
            removeSnackbar: action,
            storeDisplayedNotification: action,
            onWindowResize: action,
            reset: action,
            setCurrentAnnouncement: action,
            setWasAnnouncementDownloaded: action,
            setLanguage: action,
            setHasNewNewsData: action,
            setShowNavigationBar: action,
            setHasNewAssembliesData: action,
            setContentWidth: action,
            setIsLegendDisplayed: action,
            setIsNewsInsertedEventLoaded: action,
            printStatusMessage: action,
            printFixedErrorMessage: action,
            setIsAddNewsDocumentClicked: action,
            setIsNewsPublishModalDisplayed: action,
            setIsCustomerUserReset: action,
            setDbNewsFiles: action,
            setTenantItemName: action,
            setIsDemoModeTicketingActive: action,
            calculateScreenSize: action,
            setIsHeaderDisplayed: action
        });
    }

    getHeaderHeight = () => {
        if (window.innerWidth <= this.mobileWidth) {
            return headerHeights.mobile;
        } else if (window.innerWidth > this.mobileWidth && window.innerWidth <= this.tabletWidth) {
            return headerHeights.tablet;
        } else {
            return headerHeights.desktop;
        }
    };

    setAgendaItemPaneWidthRatio = (ratio: number) => {
        this.agendaItemPaneWidthRatio = ratio;
    };

    setInvoicePaneWidthRatio = (ratio: number) => {
        this.invoicePaneWidthRatio = ratio;
    };

    setSplitPaneWidth = (width: number) => {
        this.splitPaneWidth = width;
    };

    setIsResettingInvoicePaneWidth = (isResettingInvoicePaneWidth: boolean) => {
        this.isResettingInvoicePaneWidth = isResettingInvoicePaneWidth;
    };

    setIsResizeObserverDisabled = (isResizeObserverDisabled: boolean) => {
        this.isResizeObserverDisabled = isResizeObserverDisabled;
    };

    resetInvoicePaneWidth = () => {
        this.setIsResettingInvoicePaneWidth(true);
        this.setInvoicePaneWidth(DEFAULT_INVOICE_PANE_WIDTH);
        this.setIsResettingInvoicePaneWidth(false);
    };

    setInvoicePaneWidth = (width: number) => {
        this.invoicePaneWidth = width;
        this.invoicePaneScreenSize = this.calculateScreenSize(width);
        this.saveInvoicePaneWidthUserSetting(width);
    };


    saveInvoicePaneWidthUserSetting = async (invoicePaneWidth: number) => {
        let newInvoicePaneWidthRatio = invoicePaneWidth / this.splitPaneWidth;
        this.invoicePaneWidthRatio = newInvoicePaneWidthRatio;

        let success = false;

        try {
            const upsertAssemblyUserAuditObject: ums_usersettings_insert_input[] = [
                {
                    value: newInvoicePaneWidthRatio.toString(),
                    valuetype: 2,
                    key: "PORTAL.INVOICE_PANE_WIDTH_RATIO"
                }
            ];

            await apolloClientInstance.mutate<UpsertUsersetting, UpsertUsersettingVariables>({
                mutation: UPSERT_USERSETTING,
                variables: { objects: upsertAssemblyUserAuditObject }
            });
        } catch (error) {
            console.error("Error saving setting: ", error);
        }

        success = true;

        return success;
    };

    setInvoiceFormContainerHeight = (height: number) => {
        this.invoiceFormContainerHeight = height;
    };

    setInvoiceFormHeight = (height: number) => {
        this.invoiceFormHeight = height;
    };

    setInvoiceFilePreviewWidth = (width: number) => {
        this.invoiceFilePreviewWidth = width;
    };

    setShowProfileAppbarButton = (showProfileAppbarButton: boolean) => {
        this.showProfileAppbarButton = showProfileAppbarButton;
    };

    setShowSearchAppbarButton = (showSearchAppbarButton: boolean) => {
        this.showSearchAppbarButton = showSearchAppbarButton;
    };

    setSearchbar = (toggle: boolean) => {
        this.setHeaderHeight(toggle ? headerHeights.desktop : undefined);
        this.showSearchbar = toggle;
    };

    setIsMobile = (isMobile: boolean) => {
        this.isMobile = isMobile;
    };


    setHeaderHeight(overwriteHeight?: number) {
        this.headerHeight =
            overwriteHeight !== undefined
                ? overwriteHeight
                : this.isMobile
                ? headerHeights.mobile
                : headerHeights.desktop;
    }

    setCloseAppBar = (closeAppBar: boolean) => {
        this.closeAppbar = closeAppBar;
    };

    setBackgroundColor = (backgroundColor: string) => {
        this.backgroundColor = backgroundColor;
    };

    setFinanceListExpandedItems = (newList: string[] | undefined) => {
        this.financeListExpandedItems = newList;
    };

    setFinanceListSelectionId = (newSelectionId: string | undefined) => {
        this.financeListSelectionId = newSelectionId;
    };

    setRealestatesViewType = (newViewType: RealestatesListType) => {
        this.realestatesViewType = newViewType;
    };

    setShouldShowLoginNotification = (shouldShow: boolean) => {
        this.shouldShowLoginNotification = shouldShow;
    };

    setRerenderNavigationBar = (rerenderNavigationBar: boolean) => {
        this.rerenderNavigationBar = rerenderNavigationBar;
    };

    enqueueSnackbar = (note: Notification) => {
        this.notifications.push({
            key: new Date().getTime() + Math.random(),
            ...note
        });
    };

    removeSnackbar = (note: Notification) => {
        this.notifications = this.notifications.filter((not) => not.key !== note.key);
    };

    storeDisplayedNotification = (key: number) => {
        this.displayedNotifications = [...this.displayedNotifications, key];
    };


    private onWindowResize = () => {
        this.setIsMobile(window.innerWidth <= 768);

        runInAction(() => {
            this.currentScreenSize = this.calculateScreenSize(window.innerWidth);
        });

        this.setHeaderHeight(this.getHeaderHeight());
    };

    reset = () => {
        this.realestatesViewType = RealestatesListType.GRID;
        this.financeListSelectionType = undefined;
        this.financeListSelectionId = undefined;
        this.financeListExpandedItems = undefined;
        this.shouldShowLoginNotification = false;
        this.notifications = [];
        this.displayedNotifications = [];
        this.currentAnnouncement = undefined;
        this.wasAnnouncementDownloaded = false;
    };

    setCurrentAnnouncement = (announcement: Announcement | undefined) => {
        this.currentAnnouncement = announcement;
    };

    setWasAnnouncementDownloaded = (wasAnnouncementDownloaded: boolean) => {
        this.wasAnnouncementDownloaded = wasAnnouncementDownloaded;
    };

    setLanguage = (language: SupportedLanguage) => {
        this.language = language;
    };

    setHasNewNewsData = (hasNewNewsEntry: boolean) => {
        this.hasNewNewsData = hasNewNewsEntry;
    };

    setShowNavigationBar = (showNavigationBar: boolean) => {
        this.showNavigationBar = showNavigationBar;
    };

    setHasNewAssembliesData = (hasNewAssembliesData: boolean) => {
        this.hasNewAssembliesData = hasNewAssembliesData;
    };

    setContentWidth = (contentWidth: number) => {
        this.contentWidth = contentWidth;
    };

    setIsLegendDisplayed = (isLegendDisplayed: boolean) => {
        this.isLegendDisplayed = isLegendDisplayed;
    };

    setIsNewsInsertedEventLoaded = (isNewsInsertedEventLoaded: boolean) => {
        this.isNewsInsertedEventLoaded = isNewsInsertedEventLoaded;
    };


    printStatusMessage = (message: string, msgType: MessageType, milliseconds?: number) => {
        this.enqueueSnackbar({
            content: message,
            options: {
                type: msgType,
                autoClose: msgType === MessageType.ERROR ? false : milliseconds ? milliseconds : 3000
            }
        });
    };
    printFixedErrorMessage = (message: React.ReactNode | string, additionalClassName?: string) => {
        let className = "fixed-error-status-message";

        if (additionalClassName) {
            className += ` ${additionalClassName}`;
        }

        this.enqueueSnackbar({
            content: message,
            options: {
                type: MessageType.ERROR,
                autoClose: false,
                className
            }
        });
    };

    setIsAddNewsDocumentClicked = (isAddNewsDocumentClicked: boolean) => {
        this.isAddNewsDocumentClicked = isAddNewsDocumentClicked;
    };

    setIsNewsPublishModalDisplayed = (isNewsPublishModalDisplayed: boolean) => {
        this.isNewsPublishModalDisplayed = isNewsPublishModalDisplayed;
    };

    setIsCustomerUserReset = (isCustomerUserReset: boolean) => {
        this.isCustomerUserReset = isCustomerUserReset;
    };

    setDbNewsFiles = (dbNewsFiles: db_newsfiles[]) => {
        this.dbNewsFiles = dbNewsFiles;
    };

    setTenantItemName = (tenantItemName: string) => {
        this.tenantItemName = tenantItemName;
    };

    setIsDemoModeTicketingActive = (isDemoModeTicketingActive: boolean) => {
        this.isDemoModeTicketingActive = isDemoModeTicketingActive;
    };

    calculateScreenSize(width: number): SCREEN_SIZE {
        if (width <= 320) {
            return SCREEN_SIZE.MOBILE_S;
        } else if (width <= 425) {
            return SCREEN_SIZE.MOBILE_M;
        } else if (width <= 500) {
            return SCREEN_SIZE.MOBILE_L;
        } else if (width <= 768) {
            return SCREEN_SIZE.TABLET;
        } else if (width <= 1024) {
            return SCREEN_SIZE.LAPTOP;
        } else if (width <= 1440) {
            return SCREEN_SIZE.LAPTOP_L;
        } else {
            return SCREEN_SIZE.DESKTOP;
        }
    }

    setIsHeaderDisplayed = (isHeaderDisplayed: boolean) => {
        this.isHeaderDisplayed = isHeaderDisplayed;
    };
}
